/**
 * Notes endpoints for the API contract.
 *
 * These endpoints manage user notes attached to audio conversions/chapters.
 * Legacy API has no OpenAPI schema (responses: never), so schemas are defined
 * from scratch based on the Python models and client usage.
 *
 * @see packages/core/api-contract/README.md for the full endpoint list
 */
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { Effect, Schema, SchemaTransformation } from 'effect';
import { UserAuthMiddleware } from './middleware';
import { BadRequestError, NotFoundError, InternalServerError } from './errors';

// =============================================================================
// SCHEMAS
// =============================================================================

export const NoteResponse = Schema.Struct({
  id: Schema.String,
  legacy_id: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  title: Schema.String,
  body: Schema.String,
  audio_conversion_id: Schema.String,
  user_id: Schema.String,
  created_at_ms: Schema.Int,
  updated_at_ms: Schema.Int,
  chapter_id: Schema.NullOr(Schema.String).annotate({
    description:
      'DEPRECATED: legacy chapter note anchor. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  chapter_audio_offset_ms: Schema.NullOr(Schema.Int).annotate({
    description:
      'DEPRECATED: legacy chunk-relative note offset. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  chapter_word_offset: Schema.NullOr(Schema.Int).annotate({
    description:
      'DEPRECATED: legacy chapter word offset. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  tts_id: Schema.NullOr(Schema.String).annotate({
    description:
      'DEPRECATED: legacy stored TTS chunk ID. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  doc_pack_page: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  doc_pack_char_start: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  doc_pack_char_end: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  deleted_at_ms: Schema.NullOr(Schema.Int),
});

export const NoteUpsertInput = Schema.Struct({
  id: Schema.optional(Schema.String),
  title: Schema.String,
  body: Schema.String,
  audio_conversion_id: Schema.String,
  created_at_ms: Schema.optional(Schema.Int),
  chapter_id: Schema.optional(Schema.NullOr(Schema.String)).annotate({
    description:
      'DEPRECATED: legacy chapter note anchor. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  chapter_audio_offset_ms: Schema.optional(Schema.NullOr(Schema.Int)).annotate({
    description:
      'DEPRECATED: legacy chunk-relative note offset. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  chapter_word_offset: Schema.optional(Schema.NullOr(Schema.Int)).annotate({
    description:
      'DEPRECATED: legacy chapter word offset. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  tts_id: Schema.optional(Schema.NullOr(Schema.String)).annotate({
    description:
      'DEPRECATED: legacy stored TTS chunk ID. New notes should use doc-pack page/char fields.',
    jsonSchema: { deprecated: true },
  }),
  doc_pack_page: Schema.optional(Schema.NullOr(Schema.Int)),
  doc_pack_char_start: Schema.optional(Schema.NullOr(Schema.Int)),
  doc_pack_char_end: Schema.optional(Schema.NullOr(Schema.Int)),
});

// URL params come as strings — transform to proper types
const BooleanFromString = Schema.String.pipe(
  Schema.decodeTo(
    Schema.Boolean,
    SchemaTransformation.transform({
      decode: (s) => s === 'true',
      encode: (b) => (b ? 'true' : 'false'),
    }),
  ),
);

export const ListNotesUrlParams = Schema.Struct({
  audio_conversion_id: Schema.optional(Schema.String),
  cursor_id: Schema.optional(Schema.String),
  limit: Schema.optional(Schema.FiniteFromString),
  include_deleted: Schema.optional(BooleanFromString),
});

// =============================================================================
// RESPONSE ENVELOPES
// =============================================================================

const ListNotesData = Schema.Struct({
  notes: Schema.Array(NoteResponse),
  cursor_id: Schema.NullOr(Schema.String),
});

// Matches the legacy Python API response: {status: "success", data: {notes, cursor_id}}
// @see submodules/backend/apps/api/listening/note/note_routes.py
const ListNotesResponse = Schema.Struct({
  status: Schema.Literal('success'),
  data: ListNotesData,
});

// =============================================================================
// ENDPOINTS
// =============================================================================

/**
 * GET /notes/
 * List notes for the current user with cursor pagination.
 */
const listNotesEndpoint = HttpApiEndpoint.get('ListNotes', '/', {
  query: ListNotesUrlParams,
  success: ListNotesResponse,
  error: [BadRequestError, InternalServerError],
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'List notes')
  .annotate(
    OpenApi.Description,
    'List notes for the current user. Supports filtering by audio_conversion_id and cursor-based pagination.',
  );

/**
 * POST /notes/upsert-many
 * Batch create or update notes.
 * Notes with an `id` are updated; notes without are created.
 */
const upsertManyNotesEndpoint = HttpApiEndpoint.post(
  'UpsertManyNotes',
  '/upsert-many',
  {
    payload: Schema.Struct({ notes: Schema.Array(NoteUpsertInput) }),
    success: Schema.Struct({ data: Schema.Array(NoteResponse) }),
    error: [BadRequestError, NotFoundError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Upsert notes')
  .annotate(
    OpenApi.Description,
    'Batch create or update notes. Notes with an id field are updated; notes without are created. Max 50 notes per request, max 5KB per note.',
  );

/**
 * DELETE /notes/:noteId
 * Soft-delete a note by setting deleted_at.
 */
const deleteNoteEndpoint = HttpApiEndpoint.delete('DeleteNote', '/:noteId', {
  params: Schema.Struct({ noteId: Schema.String }),
  success: Schema.Void.pipe(HttpApiSchema.status(204)),
  error: [NotFoundError, InternalServerError],
})
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Delete a note')
  .annotate(OpenApi.Description, 'Soft-delete a note by setting deleted_at.');

// =============================================================================
// GROUP
// =============================================================================

export const notesGroup = HttpApiGroup.make('Note')
  .add(listNotesEndpoint)
  .add(upsertManyNotesEndpoint)
  .add(deleteNoteEndpoint)
  .prefix('/notes');
